import { FunctionsHttpError } from '@supabase/supabase-js';

import { AggregationIntervall } from '@/features/charts/components/create-chart-type-selection';
import { supabase } from '@/lib/supabase';
import { KpiForecast } from '@/shared/types/api';

export type PeriodType = 'monthly' | 'quarterly' | 'yearly';

export const getMonthsBackForPeriodType = (periodType: PeriodType) => {
  switch (periodType) {
    case 'monthly':
      return 3;
    case 'quarterly':
      return 12;
    case 'yearly':
      return 36;
  }
};

export const periodTypeToAggregationInterval = (
  periodType: PeriodType,
): AggregationIntervall => {
  switch (periodType) {
    case 'monthly':
      return 'monthly';
    case 'quarterly':
      return 'quarterly';
    case 'yearly':
      return 'yearly';
  }
};

export const aggregationIntervalToPeriodType = (
  aggregationInterval: AggregationIntervall,
): PeriodType | undefined => {
  switch (aggregationInterval) {
    case 'monthly':
      return 'monthly';
    case 'quarterly':
      return 'quarterly';
    case 'yearly':
      return 'yearly';
  }
};

export const periodTypeToMonths = (periodType: PeriodType) => {
  switch (periodType) {
    case 'monthly':
      return 1;
    case 'quarterly':
      return 3;
    case 'yearly':
      return 12;
  }
};

export interface ForecastsForPeriodType {
  period_type: PeriodType;
  forecasts: KpiForecast[];
}

export interface KpiForecastsResponse {
  forecasts: ForecastsForPeriodType[];
  from_cache: boolean;
}

export type ForecastErrorCode =
  | 'invalid_request_body'
  | 'failed_to_fetch_historical_data'
  | 'no_historical_data_available_for_this_kpi'
  | 'not_enough_data'
  | 'unauthorized'
  | 'unknown_error';

export class ForecastError extends Error {
  constructor(
    public code: ForecastErrorCode,
    message?: string,
  ) {
    super(message || code);
    this.name = 'ForecastError';
  }
}

export async function getKpiForecasts(
  kpiId: number,
  projectId: number,
  kpiVariation: 'derivative' | 'standard' = 'standard',
): Promise<KpiForecastsResponse> {
  // Get the current session to ensure JWT is available
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError) {
    throw new ForecastError(
      'unauthorized',
      `Session error: ${sessionError.message}. Please log in again.`,
    );
  }

  if (!session) {
    throw new ForecastError(
      'unauthorized',
      'No authentication session found. Please log in again.',
    );
  }

  if (!session.access_token) {
    throw new ForecastError(
      'unauthorized',
      'Access token missing. Please log in again.',
    );
  }

  const { data, error } = await supabase.functions.invoke('kpi-forecasts', {
    body: { kpi_id: kpiId, project_id: projectId, kpi_variation: kpiVariation },
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (error) {
    if (error instanceof FunctionsHttpError) {
      // Handle 401 Unauthorized explicitly
      if (error.context.status === 401) {
        let errorMessage = 'Authentication failed. Please log in again.';
        try {
          const errorResponse = await error.context.json();
          const errorCode = errorResponse.error as string;
          // Map specific error codes to user-friendly messages
          if (errorCode === 'missing_authorization_header') {
            errorMessage = 'Authorization header missing. Please log in again.';
          } else if (errorCode === 'invalid_jwt_format') {
            errorMessage =
              'Invalid authentication token format. Please log in again.';
          } else if (errorCode === 'invalid_jwt') {
            errorMessage =
              'Authentication token is invalid or expired. Please log in again.';
          } else if (errorCode === 'user_not_found') {
            errorMessage = 'User not found. Please log in again.';
          }
        } catch {
          // Failed to parse unauthorized error response
        }
        throw new ForecastError('unauthorized', errorMessage);
      }

      try {
        const errorResponse = await error.context.json();
        const errorCode = errorResponse.error as ForecastErrorCode;
        throw new ForecastError(errorCode);
      } catch {
        throw new ForecastError(
          'unknown_error',
          `HTTP ${error.context.status}: ${error.message}`,
        );
      }
    }
    throw new ForecastError(
      'unknown_error',
      error.message || 'Unknown error occurred',
    );
  }

  return data as KpiForecastsResponse;
}
